import React, { useState } from "react"

import classNames from "src/classNames"

import { gql, useQuery } from "hooks/urql"

import LocalTime from "components/LocalTime"
import NotificationContainer from "components/NotificationContainer"
import Pagination from "components/Pagination"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import HideButton from "./HideButton"
import ResolveButton from "./ResolveButton"

const FlaggedContent = () => {
  const [page, setPage] = useState(1)
  const [result] = useQuery({
    query: flaggedMessagesQuery,
    variables: { page },
  })

  return (
    <UrqlLoadingIndicator result={result}>
      {({ data: { flaggedMessages } }) => (
        <>
          <NotificationContainer />

          <div className="overflow-x-auto">
            <table className="table">
              <thead className="text-primary">
                <tr>
                  <th>Status</th>
                  <th>Author</th>
                  <th>Flagged By</th>
                  <th className="min-w-[10rem]">Flagged Content</th>
                  <th className="min-w-[7rem]">Sent At</th>
                  <th>Seen By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flaggedMessages.data.map(message => (
                  <tr
                    key={message.id}
                    className={classNames(
                      !message.resolved && "bg-red-50 font-semibold"
                    )}
                  >
                    <td className="align-top">
                      <div>{message.hidden ? "hidden" : "visible"}</div>
                      <div>{message.resolved ? "resolved" : "unresolved"}</div>
                    </td>
                    <td className="align-top">
                      <a
                        href={message.author.showPath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {message.author.messagingDisplayName}
                      </a>
                    </td>
                    <td className="align-top">
                      {message.flaggedBy
                        ? message.flaggedBy.messagingDisplayName
                        : "system"}
                    </td>
                    <td className="align-top">
                      <p>{message.content}</p>
                      {message.attachment && (
                        <img
                          src={message.attachment.previewUrl}
                          alt={message.attachment.filename}
                          className="sm:max-w-sm"
                        />
                      )}
                    </td>
                    <td className="align-top">
                      <span>
                        <LocalTime timestamp={message.createdAt} />
                      </span>
                    </td>
                    <td className="align-top">
                      <ul className="list-none px-0">
                        {message.readRecipients.map(user => (
                          <li key={user.id}>
                            <a
                              href={user.showPath}
                              onClick={e => e.stopPropagation()}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {user.messagingDisplayName}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="flex flex-col items-center space-y-2 align-top">
                      <a
                        className="btn btn-info btn-sm"
                        href={message.messageThread.showPath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Go To Thread
                      </a>
                      <HideButton id={message.id} hidden={message.hidden} />
                      <ResolveButton
                        id={message.id}
                        resolved={message.resolved}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={flaggedMessages.currentPage}
            totalPages={flaggedMessages.totalPages}
            setPage={setPage}
          />
        </>
      )}
    </UrqlLoadingIndicator>
  )
}

const flaggedMessagesQuery = gql`
  query FlaggedMessages($page: Int!) {
    flaggedMessages(page: $page) {
      currentPage
      totalPages
      data {
        id
        resolved
        hidden
        content
        createdAt
        author {
          id
          messagingDisplayName
          showPath
          avatar {
            thumbUrl
          }
        }
        flaggedBy {
          messagingDisplayName
        }
        messageThread {
          id
          showPath
        }
        attachment {
          filename
          downloadUrl
          previewUrl
        }
        readRecipients {
          id
          type
          showPath
          messagingDisplayName
        }
      }
    }
  }
`

export default FlaggedContent
